'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export interface CartItem {
  id: string;
  name: string;
  slug: string;
  price: number;
  original_price: number;
  image: string;
  quantity: number;
  /**
   * Tồn kho tại lúc thêm vào giỏ. Optional vì giỏ cũ trong localStorage không
   * có field này. Backend vẫn là nơi chốt cuối (trả 409 nếu thiếu hàng); trần
   * ở đây chỉ để giỏ không tích được nhiều hơn số lượng đang có.
   */
  stock?: number;
}

/** Giới hạn cứng của OrderService phía backend (clamp 1..99). */
export const CART_MAX_QUANTITY = 99;

interface CartContextType {
  items: CartItem[];
  addToCart: (item: Omit<CartItem, 'quantity'>, quantity?: number) => boolean;
  removeFromCart: (id: string) => void;
  removeManyFromCart: (ids: string[]) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

/**
 * Trần số lượng cho một dòng giỏ: min(tồn kho, 99).
 * Giỏ cũ trong localStorage không có `stock` -> chỉ áp trần 99, để backend chốt.
 */
function maxQuantityFor(stock: number | undefined): number {
  if (stock === undefined || stock === null) return CART_MAX_QUANTITY;
  const available = Number(stock);
  if (!Number.isFinite(available)) return CART_MAX_QUANTITY;
  return Math.max(0, Math.min(CART_MAX_QUANTITY, Math.floor(available)));
}

/**
 * Giỏ lưu trước đây có thể chứa id kèm khoảng trắng đệm (products.id là CHAR(36)
 * nên SQL Server pad space cho id ngắn hơn 36 ký tự). Id lệch làm backend không
 * tìm thấy sản phẩm và trả 409 "không còn khả dụng" lúc đặt hàng.
 */
function normalizeStoredItems(value: unknown): CartItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => ({ ...(item as CartItem), id: String((item as CartItem)?.id ?? '').trim() }))
    .filter((item) => item.id.length > 0);
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'dntech_cart';

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (stored) {
        setItems(normalizeStoredItems(JSON.parse(stored)));
      }
    } catch {
      // ignore
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore
    }
  }, [items, hydrated]);

  const addToCart = useCallback(
    (item: Omit<CartItem, 'quantity'>, quantity = 1) => {
      if (authLoading || !user) {
        return false;
      }

      const requested = Math.max(1, Math.floor(Number(quantity) || 1));
      const ceiling = maxQuantityFor(item.stock);
      if (ceiling <= 0) {
        return false;
      }

      setItems((prev) => {
        const existing = prev.find((i) => i.id === item.id);
        if (existing) {
          // Tồn kho mới nhất đi kèm lần thêm này, dùng nó thay cho giá trị cũ.
          return prev.map((i) =>
            i.id === item.id
              ? {
                  ...i,
                  ...item,
                  quantity: Math.min(i.quantity + requested, ceiling),
                }
              : i
          );
        }
        return [...prev, { ...item, quantity: Math.min(requested, ceiling) }];
      });
      return true;
    },
    [authLoading, user]
  );

  const removeFromCart = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const removeManyFromCart = useCallback((ids: string[]) => {
    if (!ids.length) return;
    const removeSet = new Set(ids.map(String));
    setItems((prev) => prev.filter((i) => !removeSet.has(String(i.id))));
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    const next = Math.floor(Number(quantity) || 0);
    if (next <= 0) {
      setItems((prev) => prev.filter((i) => i.id !== id));
      return;
    }
    setItems((prev) =>
      prev.map((i) =>
        i.id === id ? { ...i, quantity: Math.min(next, maxQuantityFor(i.stock)) } : i
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        removeManyFromCart,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
